import React, { useRef, useState, useMemo, useEffect } from 'react'
import { Modal } from 'react-native'
import { ConfirmDialog } from '@trivie/ui-web/components/confirm-dialog'
import { useHistory } from 'react-router-dom'
import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Divider } from '@mui/material'
import {
  View,
  ViewStyle,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Clipboard,
} from 'react-native'
import { observer } from 'mobx-react-lite'
import { useStores } from '@trivie/core'
import { DateTime } from 'luxon'
import { PostProps } from './post.props'
import { Text } from '../text'
import { Avatar } from '../avatar'
import { SvgIcon } from '../svg-icon'
import { iconMoreHorizontal } from '../../assets'
import { TextPost } from '../text-post/text-post'
import { VideoPlayer } from '../video-player'
import { ReportPostDialog } from '../../../web/components/report-post-modal/report-post-dialog'
import { ReportSubmittedDialog } from '../../../web/components/report-submitted-dialog/report-submitted-dialog'
import ImageViewer from 'react-native-image-zoom-viewer'
// @ts-ignore
import { ReactComponent as CloseIcon } from './close.svg'
// @ts-ignore
import { ReactComponent as Heart } from './heart.svg'
// @ts-ignore
import { ReactComponent as CommentIcon } from './comment.svg'
// @ts-ignore
import { ReactComponent as PinIcon } from './pin.svg'
import { color, spacing } from '../../theme'
import { PostApi } from '@trivie/core/src/services/api-objects/PostApi'
import { useMediaQuery } from '@mui/material'

/**
 * Post component
 */
export const Post = observer((props: PostProps) => {
  const { subtitle, type, post, onTogglePin, onLike, onDelete, onComment, isDetail, noPadding } =
    props
  const { libraryStore, talkStore, userStore } = useStores()
  const { topics } = libraryStore

  const { user, getPermission, getTalkPermission } = userStore
  const { reportPost, deletePost } = talkStore

  const isMobile = !useMediaQuery('(min-width:800px)')

  const history = useHistory()

  const [showOwnMenu, setShowOwnMenu] = useState(false)
  const [showOtherMenu, setShowOtherMenu] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [reportSubmitted, setReportSubmitted] = useState(false)
  const [isShowPost, setIsShowPost] = useState(false)

  const anchorRef = React.useRef<HTMLDivElement>(null)

  const topic = topics.find((c) => c.topic.topic_id === post.topic_id)

  const totalComments = post.comment_count?.toString()
    ? post.comment_count
    : post.comments.reduce((acc: number, curr: any) => {
        acc += curr.child_comments?.length || 0
        acc += 1
        return acc
      }, 0)

  useEffect(() => {
    setIsShowPost(!userStore?.permissions?.restrict_talk || getTalkPermission('can_edit_post'))
  }, [userStore?.permissions])

  const onReport = () => {
    setReportSubmitted(false)
    reportRef.current?.open()
  }

  const onCopyPostText = () => {
    Clipboard.setString(post.text)
    setShowOwnMenu(false)
  }

  const submitReportReason = (reportValue: any) => {
    if (reportValue) {
      reportPost(post.id, reportValue)
    }
    setReportSubmitted(true)
  }

  const createdAt = DateTime.fromISO(post.created_at, { zone: 'utc' })

  const formattedPostedDate = DateTime.fromISO(post.created_at).toFormat('MMM d')

  const now = DateTime.now()
  const elapsed = now.diff(createdAt).toMillis()

  const msPerSecond = 1000
  const msPerMinute = 60 * 1000
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365

  let elapsedTime = ''

  if (elapsed < msPerMinute / 3) {
    elapsedTime = 'Just Now'
  } else if (elapsed < msPerMinute) {
    elapsedTime = `${Math.round(elapsed / msPerSecond)} s`
  } else if (elapsed < msPerHour) {
    elapsedTime = `${Math.round(elapsed / msPerMinute)} m`
  } else if (elapsed < msPerDay) {
    elapsedTime = `${Math.round(elapsed / msPerHour)} h`
  } else if (elapsed < msPerMonth) {
    elapsedTime = `${formattedPostedDate}`
  } else if (elapsed < msPerYear) {
    elapsedTime = `${formattedPostedDate}`
  } else {
    elapsedTime = `${Math.round(elapsed / msPerYear)} y`
  }

  const reportRef = useRef<any>(null)

  const OtherAndOwnMenuToggle = () => {
    const userId = post.author.id || post.author.user_id
    if (userId === user?.id || getPermission('can_delete_post')) {
      setShowOwnMenu((prevState) => !prevState)
    } else {
      setShowOtherMenu((prevState) => !prevState)
    }
  }

  const handleMenuItemClick = (index: number) => {
    if (index === 1) {
      //@ts-ignore
      onComment(post)
    } else if (index === 3) {
      onCopyPostText()
    } else if (index === 4) {
      setShowConfirmDelete(true)
    } else if (index === 5) {
      setShowOwnMenu(false)
      setShowOtherMenu(false)
    } else if (index === 6) {
      setShowReportModal(true)
      onReport()
    }

    setShowOwnMenu(false)
    setShowOtherMenu(false)
  }

  const handlePinPost = () => {
    setShowOwnMenu(false)
    setShowOtherMenu(false)
    PostApi.togglePostPin(post.id).then(() => {
      if (onTogglePin) {
        onTogglePin()
      }
    })
  }

  const [pauseVideo, setPauseVideo] = useState(false)
  const [showGallery, setShowGallery] = useState(false)
  const [galleryImages, setGalleryImages] = useState([])
  const [galleryStartIdx, setGalleryStartIdx] = useState(0)

  const handleGallery = (resources: any, index: number) => {
    setGalleryStartIdx(index)
    setShowGallery(true)
    setGalleryImages(
      resources.map((resource: any) => {
        return { url: resource.file_url }
      }),
    )
  }

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    setShowOwnMenu(false)
    setShowOtherMenu(false)
  }

  const goToTopic = (topicID: any, assignmentID: any) => {
    if (topicID && assignmentID) {
      history.push(`/library/${assignmentID}/${topicID}`)
    }
  }

  const onClickProfilePic = () => {
    return (post.author.id || post.author.user_id) === user?.id
      ? history.push('/profile/stats')
      : null
  }
  const renderAvatar = useMemo(() => {
    const _name = post.author.display_name
      ? post.author.display_name
      : `${post.author.first_name} ${post.author.last_name}`

    return (
      <div aria-label={`${_name} profile picture`} role="img" onClick={onClickProfilePic}>
        <Avatar
          style={{ marginRight: 10 }}
          customSize={36}
          source={post.author.file_url || post.author.photo}
          name={_name}
        />
      </div>
    )
  }, [
    post.author.display_name
      ? post.author.display_name
      : `${post.author.first_name} ${post.author.last_name}`,
  ])

  const renderImage = useMemo(() => {
    return post.resources ? (
      <Image
        style={{ height: 316, width: '100%', resizeMode: 'contain' }}
        source={{ uri: post?.resources && post.resources[0]?.file_url }}
      />
    ) : null
  }, [])
  const renderSImage = useMemo(() => {
    return post.resources ? (
      <Image
        style={{ height: 316, width: '100%', borderRightWidth: 2, borderColor: '#fff' }}
        source={{ uri: post?.resources[0]?.file_url }}
      />
    ) : null
  }, [])
  const renderSImage_2 = useMemo(() => {
    return post.resources ? (
      <Image
        style={{ height: 316, width: '100%', borderLeftWidth: 2, borderColor: '#fff' }}
        source={{ uri: post?.resources[1]?.file_url }}
      />
    ) : null
  }, [])

  const renderSImage_3 = useMemo(() => {
    return post.resources ? (
      <Image
        style={{
          height: 158,
          width: '100%',
          borderBottomWidth: 2,
          borderColor: '#fff',
        }}
        source={{ uri: post?.resources[1]?.file_url }}
      />
    ) : null
  }, [])
  const renderSImage_4 = useMemo(() => {
    return post.resources ? (
      <Image
        style={{ height: 158, width: '100%', borderTopWidth: 2, borderColor: '#fff' }}
        source={{ uri: post?.resources[2]?.file_url }}
      />
    ) : null
  }, [])

  const canDelete =
    getPermission('can_delete_post') ||
    (post.author.id === user?.id && createdAt.diffNow('minutes').minutes > -10)

  return (
    <>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          maxWidth: '1470px',
          paddingRight: 60,
        }}
      >
        <ConfirmDialog
          danger
          open={showConfirmDelete}
          title="Remove Post"
          message="Are you sure you want to delete this post?"
          confirmButton="Confirm"
          onConfirm={() => {
            if (onDelete) {
              onDelete(post.id)
              history.push(`/talk`)
            } else {
              deletePost(post.id).then(() => {
                history.push(`/talk`)
              })
            }
          }}
          onClose={() => setShowConfirmDelete(false)}
          handleCancel={() => setShowConfirmDelete(false)}
        />
        {/* OWN USER MENU */}
        <Popper
          open={showOwnMenu}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal={false}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              timeout={{ appear: 200, enter: 200, exit: 200 }}
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center left',
              }}
            >
              <Paper style={DROPDOWN} variant="outlined">
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList style={{ padding: 0 }} id="split-button-menu">
                    <MenuItem onClick={() => handleMenuItemClick(1)} style={MENU_ITEM_1}>
                      <Text
                        tx="Discuss.reply"
                        variant="body1Medium"
                        style={{ fontSize: 16, textAlign: 'center' }}
                      />
                    </MenuItem>
                    {/* UNCOMMENT BELOW WHEN EDIT ENDPOINTS ARE ADDED */}
                    {/* <MenuItem onClick={event => handleMenuItemClick(event, 2)} style={MENU_ITEM_1}>
                      <Text text="Edit" variant="body1Medium" style={{ fontSize: 16 }} />
                    </MenuItem>
                    <Divider /> */}

                    {getPermission('can_pin_post') ? (
                      <>
                        <Divider />
                        <MenuItem onClick={() => handlePinPost()} style={MENU_ITEM_1}>
                          <Text
                            tx={post.is_pinned ? 'Discuss.unpin' : 'Discuss.pin'}
                            variant="body1Medium"
                            style={{ fontSize: 16 }}
                          />
                        </MenuItem>
                      </>
                    ) : null}

                    <Divider />
                    <MenuItem onClick={() => handleMenuItemClick(3)} style={MENU_ITEM_1}>
                      <Text tx="Discuss.copy" variant="body1Medium" style={{ fontSize: 16 }} />
                    </MenuItem>
                    {canDelete ? (
                      <>
                        <Divider />
                        <MenuItem onClick={() => handleMenuItemClick(4)} style={MENU_ITEM_1}>
                          <Text
                            tx="Discuss.delete"
                            variant="body1Medium"
                            style={{ fontSize: 16 }}
                          />
                        </MenuItem>
                      </>
                    ) : null}
                    <Divider />
                    <MenuItem onClick={() => handleMenuItemClick(6)} style={MENU_ITEM_1}>
                      <Text
                        tx="Discuss.reportHeader"
                        variant="body1Medium"
                        style={{ fontSize: 16 }}
                      />
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => handleMenuItemClick(5)} style={MENU_ITEM_1}>
                      <Text tx="Common.cancel" variant="body1Medium" style={{ fontSize: 16 }} />
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        {/* OTHER USER MENU */}
        <Popper
          open={showOtherMenu}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal={false}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              timeout={{ appear: 200, enter: 200, exit: 200 }}
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center left',
              }}
            >
              <Paper style={DROPDOWN} variant="outlined">
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList style={{ padding: 0 }} id="split-button-menu">
                    <MenuItem onClick={(event) => handleMenuItemClick(6)} style={MENU_ITEM_1}>
                      <Text
                        tx="Discuss.reportHeader"
                        variant="body1Medium"
                        style={{ fontSize: 16, textAlign: 'center' }}
                      />
                    </MenuItem>

                    {canDelete ? (
                      <>
                        <Divider />
                        <MenuItem onClick={(event) => handleMenuItemClick(4)} style={MENU_ITEM_1}>
                          <Text
                            tx="Discuss.delete"
                            variant="body1Medium"
                            style={{ fontSize: 16 }}
                          />
                        </MenuItem>
                      </>
                    ) : null}

                    {getPermission('can_pin_post') ? (
                      <>
                        <Divider />
                        <MenuItem onClick={() => handlePinPost()} style={MENU_ITEM_1}>
                          <Text
                            tx={post.is_pinned ? 'Discuss.unpin' : 'Discuss.pin'}
                            variant="body1Medium"
                            style={{ fontSize: 16 }}
                          />
                        </MenuItem>
                      </>
                    ) : null}
                    <Divider />
                    <MenuItem onClick={() => handleMenuItemClick(3)} style={MENU_ITEM_1}>
                      <Text tx="Discuss.copy" variant="body1Medium" style={{ fontSize: 16 }} />
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => handleMenuItemClick(5)} style={MENU_ITEM_1}>
                      <Text
                        tx="Common.cancel"
                        variant="body1Medium"
                        style={{ fontWeight: 'bold', fontSize: 16 }}
                      />
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>

      <View style={{ paddingTop: 20, backgroundColor: color.palette.white }}>
        {post.is_pinned && (
          <View style={{ ...HEADER, paddingHorizontal: 14, marginBottom: 12 }}>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
              }}
            >
              <PinIcon />
              <Text
                variant="body2Emphasized"
                tx="Discuss.pinnedPost"
                style={{ color: color.shade50, marginLeft: 4 }}
              />
            </View>
            <TouchableOpacity
              dataSet={{ qat: `post_${post.id}_more_options_btn` }}
              ref={anchorRef}
              style={{ bottom: 0 }}
              onPress={() => OtherAndOwnMenuToggle()}
            >
              <div aria-label="More Post Options">
                <SvgIcon src={iconMoreHorizontal} color={color.black} size={24} />
              </div>
            </TouchableOpacity>
          </View>
        )}
        <View style={{ ...HEADER, paddingRight: 12 }}>
          <View style={HEADER_LEFT}>
            {renderAvatar}
            <View style={{ flexDirection: 'column', top: 1 }}>
              <Text
                text={
                  post.author.display_name
                    ? post.author.display_name
                    : post.author.first_name && post.author.last_name
                      ? `${post.author.first_name} ${post.author.last_name}`
                      : 'Unnamed User'
                }
                disableLink
                variant="body1Emphasized"
                style={{ fontWeight: '600' }}
              />
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text
                  numberOfLines={1}
                  style={{
                    alignSelf: 'center',
                    color: color.darkenGrey,
                    // @ts-ignore
                    cursor: post.topic_title == null ? null : 'pointer',
                  }}
                  text={post.topic_title == null ? 'General' : post.topic_title || ''}
                  variant="body1Medium"
                  onClick={() => goToTopic(post?.topic_id, topic?.assignment_id)}
                />

                <Text
                  variant="body1Medium"
                  style={{ color: color.shade50, top: -1, paddingLeft: 4, paddingRight: 4 }}
                >
                  {' '}
                  ∙{' '}
                </Text>
                <Text
                  style={{ alignSelf: 'center', color: color.darkenGrey }}
                  variant="body1Medium"
                  text={elapsedTime}
                />
              </View>
            </View>
          </View>
          {!post.is_pinned && (
            <TouchableOpacity
              // @ts-ignore
              ref={anchorRef}
              style={{ bottom: 0 }}
              dataSet={{ qat: `post_${post.id}_more_options_btn` }}
              onPress={() => OtherAndOwnMenuToggle()}
            >
              <div aria-label="More Post Options">
                <SvgIcon src={iconMoreHorizontal} color={color.black} size={24} />
              </div>
            </TouchableOpacity>
          )}
        </View>
        <View style={{ marginBottom: spacing[1], flexShrink: 'revert' }}>
          <View style={{ ...TITLE, paddingHorizontal: 14 }}>
            <Text
              disableLink
              text={subtitle}
              style={{ fontWeight: '600', fontSize: 18 }}
              variant="labelEmphasized"
            />
          </View>
        </View>
        {type === 'text' ? (
          <TextPost content={post.text} />
        ) : type === 'photo' ? (
          post.resources.length === 1 ? (
            <TouchableOpacity
              // @ts-ignore
              dataSet={{ qat: 'post_photo' }}
              style={{ paddingHorizontal: 14 }}
              onPress={() => handleGallery(post.resources, 0)}
            >
              <Text style={{ marginBottom: 10 }} text={post.text} />
              {renderImage}
            </TouchableOpacity>
          ) : post.resources.length === 2 ? (
            <>
              <Text style={{ marginBottom: 10, paddingHorizontal: 14 }} text={post.text} />
              <View style={{ paddingHorizontal: 14, flexDirection: 'row' }}>
                <TouchableOpacity
                  // @ts-ignore
                  dataSet={{ qat: 'post_photo_1' }}
                  style={{ width: '50%' }}
                  onPress={() => handleGallery(post.resources, 0)}
                >
                  {renderSImage}
                </TouchableOpacity>
                <TouchableOpacity
                  // @ts-ignore
                  dataSet={{ qat: 'post_photo_2' }}
                  style={{ width: '50%' }}
                  onPress={() => handleGallery(post.resources, 1)}
                >
                  {renderSImage_2}
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <Text style={{ marginBottom: 10, paddingHorizontal: 14 }} text={post.text} />
              <View style={{ paddingHorizontal: 14, flexDirection: 'row' }}>
                <TouchableOpacity
                  // @ts-ignore
                  dataSet={{ qat: 'post_photo_1' }}
                  style={{ width: '62%' }}
                  onPress={() => handleGallery(post.resources, 0)}
                >
                  {renderSImage}
                </TouchableOpacity>
                <View
                  style={{
                    width: '38%',
                    borderLeftWidth: 2,
                    borderColor: '#fff',
                    flexDirection: 'column',
                  }}
                >
                  <TouchableOpacity onPress={() => handleGallery(post.resources, 1)}>
                    {renderSImage_3}
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handleGallery(post.resources, 2)}>
                    {renderSImage_4}
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )
        ) : type === 'link' ? (
          <View style={{ paddingHorizontal: 14 }}>
            <Text text="Common.link" />
          </View>
        ) : type === 'video' ? (
          <>
            <Text style={{ marginBottom: 10, paddingHorizontal: 14 }} text={post.text} />
            <VideoPlayer
              source={props.video}
              isDetail={isDetail}
              alreadyPaused={pauseVideo}
              index={props.index}
              currentViewableIndex={props.currentViewableIndex}
            />
          </>
        ) : null}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
            alignItems: 'center',
            paddingTop: 20,
            paddingBottom: 16,
          }}
        >
          <TouchableOpacity
            // @ts-ignore
            dataSet={{ qat: 'like_post_btn' }}
            style={FOOTER_LEFT}
            onPress={onLike}
          >
            <Heart
              style={{
                height: 18,
                width: 18,
                fontSize: 18,
                fill: post.liked_by_current_user ? color.red : color.shade50,
              }}
            />

            <Text
              tx={post.likes?.length || 'Discuss.like'}
              style={{ marginLeft: 5, color: color.shade50 }}
              variant="body1Emphasized"
            />
          </TouchableOpacity>

          <TouchableOpacity
            // @ts-ignore
            dataSet={{ qat: 'comment_btn' }}
            disabled={!isShowPost}
            style={{ ...FOOTER_CENTER, opacity: isShowPost ? 1 : 0 }}
            onPress={() => {
              if (!isDetail && type === 'video') {
                setPauseVideo(true)
              }
              if (onComment) {
                onComment(post)
              }
            }}
          >
            <CommentIcon
              style={{
                height: 18,
                width: 18,
                fontSize: 18,
                fill: color.shade50,
              }}
            />
            <Text
              style={{ marginLeft: spacing[2], color: color.shade50 }}
              txOptions={{ count: totalComments }}
              tx={isDetail || totalComments === 0 ? 'Discuss.comment' : `Discuss.comments`}
              variant="body1Emphasized"
            />
          </TouchableOpacity>

          <></>
        </View>
      </View>
      <Modal visible={showGallery} presentationStyle="fullScreen">
        <ImageViewer
          renderHeader={() => (
            <TouchableOpacity
              // @ts-ignore
              dataSet={{ qat: 'exit_gallery' }}
              onPress={() => setShowGallery(false)}
              style={{ zIndex: 9999, position: 'absolute', top: 20, left: 20 }}
            >
              <CloseIcon />
            </TouchableOpacity>
          )}
          enablePreload
          useNativeDriver
          loadingRender={() => <ActivityIndicator color="#999" size="small" />}
          imageUrls={galleryImages}
          saveToLocalByLongPress={false}
          index={galleryStartIdx}
        />
      </Modal>
      <>
        <Popper
          open={showReportModal}
          style={{ marginLeft: 20, marginRight: 20, marginBottom: 66 }}
        />
        <ReportPostDialog
          open={showReportModal}
          onSubmit={submitReportReason}
          onClose={() => setShowReportModal(false)}
        />
      </>
      <>
        <Popper
          open={reportSubmitted}
          style={{ marginLeft: 20, marginRight: 20, marginBottom: 66 }}
        />
        <ReportSubmittedDialog
          open={reportSubmitted}
          onSubmit={() => setReportSubmitted(false)}
          onClose={() => setReportSubmitted(false)}
        />
      </>
    </>
  )
})

const HEADER: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
}
const HEADER_LEFT: ViewStyle = {
  alignItems: 'center',
  alignSelf: 'flex-start',
  flexDirection: 'row',
}
const TITLE: ViewStyle = {
  paddingTop: 16,
  paddingBottom: 12,
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  width: '100%',
}
const FOOTER_LEFT: ViewStyle = {
  alignItems: 'center',
  flexDirection: 'row',
}
const FOOTER_CENTER: ViewStyle = {
  alignItems: 'center',
  flexDirection: 'row',
  right: 16,
}

const DROPDOWN: React.CSSProperties = {
  position: 'relative',

  top: -15,
  right: 40,
  width: 130,
  borderRadius: 10,
  padding: 0,
  boxShadow: '0px 4px 8px 8px rgba(0,0,0,0.03)',
}

const MENU_ITEM: React.CSSProperties = {
  paddingBottom: 22,
  paddingTop: 22,
  paddingLeft: 18,
  paddingRight: 24,

  height: 46,
  width: '100%',
  flexDirection: 'row',
}

const MENU_ITEM_1: React.CSSProperties = {
  ...MENU_ITEM,
}
const MENU_ITEM_3: React.CSSProperties = {
  ...MENU_ITEM,
  height: 60,
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
}
